// =============================================================================
// Small reusable bits that several Profile panels share.
// Pulled out of Profile.jsx so each panel imports just what it needs.
// =============================================================================

import { C } from "../../lib/theme";
import { Card } from "../../components/Card";
import { Icon } from "../../components/Icon";

export function StatCard({ label, value, icon }) {
  return (
    <Card pad={16}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: C.cream,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon name={icon} size={15} color={C.hunter} />
        </span>
        <div>
          <div style={{ fontSize: 18, fontWeight: 900, color: C.charcoal, letterSpacing: "-0.3px" }}>{value}</div>
          <div style={{ fontSize: 10.5, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>{label}</div>
        </div>
      </div>
    </Card>
  );
}

export function QuickLink({ icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        all: "unset",
        cursor: "pointer",
        padding: "14px 16px",
        borderRadius: 12,
        background: C.cream,
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontSize: 13,
        fontWeight: 700,
        color: C.charcoal,
      }}
    >
      <Icon name={icon} size={15} color={C.hunter} />
      {label}
    </button>
  );
}

// Toggle switch — visual parity with mobile's Switch component.
export function Toggle({ on, onChange, disabled }) {
  return (
    <button
      onClick={() => !disabled && onChange(!on)}
      disabled={disabled}
      style={{
        all: "unset",
        cursor: disabled ? "not-allowed" : "pointer",
        width: 40,
        height: 22,
        borderRadius: 11,
        background: on ? C.hunter : C.dustDk,
        position: "relative",
        transition: "background 0.15s",
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 2,
          left: on ? 20 : 2,
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: C.white,
          transition: "left 0.15s",
          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
        }}
      />
    </button>
  );
}

export function SettingRow({ icon, title, children }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 22px", borderBottom: `1px solid ${C.cream}` }}>
      <span style={{ width: 32, height: 32, borderRadius: 10, background: C.cream, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon name={icon} size={14} color={C.hunter} />
      </span>
      <div style={{ flex: 1, fontSize: 13, fontWeight: 700, color: C.charcoal }}>{title}</div>
      {children}
    </div>
  );
}

export function DeleteAccountRow({ onOpenDelete }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "4px 0" }}>
      <span style={{ width: 32, height: 32, borderRadius: 10, background: C.cream, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon name="alert-octagon" size={14} color={C.red} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: C.red }}>Delete account</div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 2, lineHeight: 1.5 }}>
          Permanently remove your Kapio account and personal data. Orders with open escrow or
          active fulfilment must be resolved first.
        </div>
      </div>
      <button
        onClick={onOpenDelete}
        style={{
          all: "unset",
          cursor: "pointer",
          padding: "8px 14px",
          background: C.redBg,
          color: C.red,
          border: `1px solid ${C.red}`,
          borderRadius: 10,
          fontSize: 12,
          fontWeight: 800,
          flexShrink: 0,
        }}
      >
        Delete…
      </button>
    </div>
  );
}

// Interactive row for phone verification. Collapses to a plain status row once
// verified; otherwise shows a "Verify now" CTA that opens the SMS OTP modal.
export function PhoneVerifyRow({ user, onOpenVerify }) {
  const verified = !!user?.phone_verified;
  const hasPhone = !!user?.phone;
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "4px 0" }}>
      <span style={{ width: 32, height: 32, borderRadius: 10, background: C.cream, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon name="smartphone" size={14} color={verified ? C.hunter : C.slate} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.charcoal }}>Phone verification</div>
          {verified && (
            <span style={{ fontSize: 10, fontWeight: 800, color: C.hunter, background: C.greenBg, padding: "2px 8px", borderRadius: 10, letterSpacing: 0.3, textTransform: "uppercase" }}>
              Verified
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 2, lineHeight: 1.5 }}>
          {verified
            ? `${user.phone} — secures mobile-money payouts and account recovery.`
            : hasPhone
              ? `${user.phone} is on file but not verified. Verify to secure payouts and account recovery.`
              : "Add and verify your phone to secure mobile-money payouts and account recovery."}
        </div>
      </div>
      {!verified && (
        <button
          onClick={onOpenVerify}
          style={{
            all: "unset",
            cursor: "pointer",
            padding: "8px 14px",
            background: C.hunter,
            color: C.dust,
            borderRadius: 10,
            fontSize: 12,
            fontWeight: 800,
            flexShrink: 0,
          }}
        >
          Verify now
        </button>
      )}
    </div>
  );
}

export function LinkRow({ icon, title, body, href, external }) {
  return (
    <a
      href={href}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "8px 10px",
        borderRadius: 10,
        textDecoration: "none",
        border: "1px solid rgba(196,193,183,0.4)",
        background: C.white,
        cursor: "pointer",
        transition: "background 0.15s",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = C.cream)}
      onMouseLeave={(e) => (e.currentTarget.style.background = C.white)}
    >
      <span style={{ width: 32, height: 32, borderRadius: 10, background: C.cream, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon name={icon} size={14} color={C.hunter} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 800, color: C.charcoal }}>{title}</div>
        <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2, lineHeight: 1.5, wordBreak: "break-all" }}>{body}</div>
      </div>
      <Icon name={external ? "arrow-right" : "chevron-right"} size={14} color={C.muted} style={{ marginTop: 8 }} />
    </a>
  );
}
